import { AppleLogo, LinuxLogo, WindowsLogo, GithubLogo, XLogo, DiscordLogo, Heart, ArrowsClockwise } from "@phosphor-icons/react";
import { Fingerprint, Boxes, Network, Gem } from "lucide-react";
import { motion, useMotionValue, useSpring, useTransform, useScroll, useMotionValueEvent, AnimatePresence } from "motion/react";
import {
	Suspense,
	lazy,
	useState,
	useCallback,
	useEffect,
	useRef,
	useMemo,
} from "react";
import logoLockup from "@/assets/images/basilica-logo-lockup.svg";
import { BasilicaLogo } from "@/components/basilica-logo";
import { cn } from "@/lib/utils";
import { GlobeSpinner } from "./globe-spinner";

// Hook to detect scroll direction
function useScrollDirection(containerRef: React.RefObject<HTMLDivElement | null>) {
	const [hidden, setHidden] = useState(false);
	const { scrollY } = useScroll({ container: containerRef });
	const lastScrollY = useRef(0);

	useMotionValueEvent(scrollY, "change", (latest) => {
		const previous = lastScrollY.current;
		const direction = latest > previous ? "down" : "up";

		// Only hide after scrolling down past 100px
		if (direction === "down" && latest > 100) {
			setHidden(true);
		} else if (direction === "up") {
			setHidden(false);
		}

		lastScrollY.current = latest;
	});

	return hidden;
}
// Ambient mouse-following glow
function AmbientGlow() {
	const mouseX = useMotionValue(0);
	const mouseY = useMotionValue(0);

	const smoothX = useSpring(mouseX, { damping: 50, stiffness: 100 });
	const smoothY = useSpring(mouseY, { damping: 50, stiffness: 100 });

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			mouseX.set(e.clientX);
			mouseY.set(e.clientY);
		};

		window.addEventListener("mousemove", handleMouseMove, { passive: true });
		return () => window.removeEventListener("mousemove", handleMouseMove);
	}, [mouseX, mouseY]);

	return (
		<motion.div
			className="pointer-events-none fixed inset-0 z-0"
			style={{
				background: useTransform(
					[smoothX, smoothY],
					([x, y]) =>
						`radial-gradient(800px circle at ${x}px ${y}px, rgba(180, 140, 80, 0.04), transparent 60%)`
				),
			}}
		/>
	);
}

// Lazy load Earth component
const Earth = lazy(() => import("./earth"));

// Special characters for the scramble effect
const scrambleChars = "#@$%&*!?+=-~^<>|/\\[]{}:;";

// Custom easing function - exponential ease out for dramatic slow down
const easeOutExpo = (t: number): number => {
	return t === 1 ? 1 : 1 - 2 ** (-10 * t);
};

// Individual character with scramble animation
function ScrambleChar({
	char,
	isSpace,
	initialDelay = 0,
}: {
	char: string;
	isSpace: boolean;
	initialDelay?: number;
}) {
	const [displayChar, setDisplayChar] = useState(char);
	const [opacity, setOpacity] = useState(1);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isAnimatingRef = useRef(false);

	const runScramble = useCallback((duration: number = 800) => {
		if (isSpace || char === "," || char === ".") return;
		if (isAnimatingRef.current) return;

		isAnimatingRef.current = true;
		const startTime = performance.now();
		const totalIterations = 12;
		let iteration = 0;

		const scheduleNext = () => {
			const elapsed = performance.now() - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const eased = easeOutExpo(progress);

			const minDelay = 15;
			const maxDelay = 180;
			const delay = minDelay + (maxDelay - minDelay) * eased;

			setOpacity(0.7 + 0.3 * (0.5 + 0.5 * Math.sin(iteration * 0.8)));

			if (iteration < totalIterations && progress < 0.95) {
				const shouldSettle = Math.random() < eased * eased;
				if (shouldSettle && progress > 0.3) {
					setDisplayChar(char);
				} else {
					const randomChar = scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
					setDisplayChar(randomChar);
				}
				iteration++;
				timeoutRef.current = setTimeout(scheduleNext, delay);
			} else {
				setDisplayChar(char);
				setOpacity(1);
				isAnimatingRef.current = false;
			}
		};

		setDisplayChar(scrambleChars[Math.floor(Math.random() * scrambleChars.length)]);
		timeoutRef.current = setTimeout(scheduleNext, 15);
	}, [char, isSpace]);

	// Initial cascade animation on mount
	useEffect(() => {
		if (isSpace || char === "," || char === ".") return;

		const timer = setTimeout(() => {
			runScramble(700);
		}, initialDelay);

		return () => clearTimeout(timer);
	}, [char, initialDelay, isSpace, runScramble]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	if (isSpace) {
		return <span aria-hidden="true"> </span>;
	}

	return (
		<span
			aria-hidden="true"
			className="transition-opacity duration-75"
			style={{ opacity }}
		>
			{displayChar}
		</span>
	);
}

// Hover scramble text component
function ScrambleText({
	text,
	className,
	cascadeOnMount = true,
}: {
	text: string;
	className?: string;
	cascadeOnMount?: boolean;
}) {
	const chars = useMemo(
		() => text.split("").map((char, index) => ({ char, index, id: `${char}-${index}` })),
		[text]
	);

	return (
		<span className={cn("cursor-default", className)}>
			<span className="sr-only">{text}</span>
			{chars.map(({ char, index, id }) => (
				<ScrambleChar
					key={id}
					char={char}
					isSpace={char === " "}
					initialDelay={cascadeOnMount ? 400 + index * 35 : 0}
				/>
			))}
		</span>
	);
}

// Stat pill component - bordered pill for technical terms
function StatPill({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
	return (
		<motion.span
			initial={{ opacity: 0, y: 10 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.4, delay }}
			className="inline-flex items-center px-4 py-2 rounded-full border border-white/20 text-sm text-white/70 font-mono"
		>
			{children}
		</motion.span>
	);
}

// Dotted divider component
function DottedDivider() {
	return (
		<div className="w-full border-b border-dotted border-white/15" />
	);
}

// Unified button component
function Button({
	icon: Icon,
	children,
	variant = "default",
	size = "default",
	type = "button",
	disabled = false,
	className,
}: {
	icon?: React.ComponentType<{ size?: number; weight?: "fill" | "regular" | "bold" }>;
	children: React.ReactNode;
	variant?: "default" | "gold" | "solid";
	size?: "default" | "large";
	type?: "button" | "submit";
	disabled?: boolean;
	className?: string;
}) {
	return (
		<motion.button
			type={type}
			disabled={disabled}
			whileHover={disabled ? {} : { scale: 1.03, y: -2 }}
			whileTap={disabled ? {} : { scale: 0.97 }}
			transition={{ type: "spring", stiffness: 400, damping: 17 }}
			className={cn(
				"group relative flex items-center justify-center gap-2.5 rounded-xl",
				"transition-all duration-200",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
				size === "large" ? "px-6 py-3.5 h-[48px]" : "px-5 py-2.5 h-[48px]",
				disabled && "cursor-not-allowed",
				variant === "gold" && !disabled && "bg-gold/15 border border-gold/30 text-gold hover:bg-gold/25 hover:border-gold/50 hover:shadow-[0_8px_32px_-8px_rgba(180,140,80,0.3)]",
				variant === "default" && !disabled && "bg-white/10 hover:bg-white/15 hover:shadow-[0_8px_32px_-8px_rgba(255,255,255,0.1)]",
				variant === "default" && disabled && "bg-white/10",
				variant === "solid" && !disabled && "bg-white text-[#0a0a0a] hover:bg-white/90 hover:shadow-[0_8px_32px_-8px_rgba(255,255,255,0.25)]",
				variant === "solid" && disabled && "bg-white/10",
				className
			)}
		>
			{Icon && (
				<span className={cn(
					"transition-transform duration-200",
					!disabled && "group-hover:scale-110",
					disabled ? "text-white/40" : variant === "gold" ? "text-gold" : variant === "solid" ? "text-[#0a0a0a]" : "text-white"
				)}>
					<Icon size={size === "large" ? 22 : 18} weight="fill" />
				</span>
			)}
			<span className={cn(
				"font-sans font-medium",
				size === "large" ? "text-base" : "text-[15px]",
				disabled ? "text-white/40" : variant === "gold" ? "text-gold" : variant === "solid" ? "text-[#0a0a0a]" : "text-white"
			)}>
				{children}
			</span>
		</motion.button>
	);
}

// Comparison table component with dotted separators and descriptions
function ComparisonTable() {
	const rows = [
		{
			aspect: "Data",
			traditional: "Their servers",
			traditionalDesc: "Your files live on computers you don't own, run by a company that might not be around\u00A0forever.",
			basilica: "Your devices",
			basilicaDesc: "Replicates directly between your machines via encrypted peer-to-peer\u00A0sync.",
		},
		{
			aspect: "Trust",
			traditional: "Company policies",
			traditionalDesc: "You're trusting a company to keep its promises, but the fine print can change at any\u00A0time.",
			basilica: "Trustless",
			basilicaDesc: "Every operation is cryptographically signed \u2014 the mathematics itself is the\u00A0guarantee.",
		},
		{
			aspect: "Access",
			traditional: "Subscription",
			traditionalDesc: "You pay every month to use your own work, and lose access the moment you\u00A0stop.",
			basilica: "Perpetual",
			basilicaDesc: "Runs natively on your hardware with nothing to renew, expire, or call home\u00A0to.",
		},
		{
			aspect: "Ownership",
			traditional: "Licensed to you",
			traditionalDesc: "You don't own the software \u2014 you have permission to use it, which can be\u00A0revoked.",
			basilica: "Owned by you",
			basilicaDesc: "The full source is yours to audit, modify, and deploy entirely on your\u00A0terms.",
		},
	];

	return (
		<div className="w-full">
			{/* Header row */}
			<div className="grid grid-cols-[140px_1fr_1fr] gap-8 pb-4">
				<div className="text-base text-white/30 font-medium" />
				<div className="text-base text-white/30 font-medium">Traditional</div>
				<div className="text-base text-gold font-medium">Basilica</div>
			</div>
			<DottedDivider />

			{/* Data rows */}
			{rows.map((row, index) => (
				<motion.div
					key={row.aspect}
					initial={{ opacity: 0, y: 10 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, margin: "-50px" }}
					transition={{ duration: 0.4, delay: index * 0.08 }}
				>
					<div className="grid grid-cols-[140px_1fr_1fr] gap-8 py-6">
						<div className="text-base text-white/30 font-medium">{row.aspect}</div>
						<div className="max-w-[360px]">
							<div className="text-base text-white/40 font-medium">{row.traditional}</div>
							<p className="mt-1.5 text-[16px] text-white/30 leading-relaxed">{row.traditionalDesc}</p>
						</div>
						<div className="max-w-[360px]">
							<div className="text-base text-white font-medium">{row.basilica}</div>
							<p className="mt-1.5 text-[16px] text-white/50 leading-relaxed">{row.basilicaDesc}</p>
						</div>
					</div>
					{index < rows.length - 1 && <DottedDivider />}
				</motion.div>
			))}
		</div>
	);
}

// Pillar card component
function PillarCard({
	icon: Icon,
	title,
	description,
	stat,
	statLabel,
	href,
	delay = 0,
}: {
	icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
	title: string;
	description: string;
	stat: string;
	statLabel: string;
	href: string;
	delay?: number;
}) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.5, delay, ease: [0.25, 1, 0.5, 1] }}
			className="group relative p-6 bg-gradient-to-b from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-xl hover:border-white/10 transition-colors duration-300"
		>
			{/* Architectural top line accent */}
			<div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

			{/* Header: Icon left, Stat right */}
			<div className="flex items-start justify-between">
				<div className="p-2.5 rounded-xl border-2 border-gold/30 text-gold">
					<Icon size={24} strokeWidth={1.5} />
				</div>
				<div className="text-right">
					<div className="text-[22px] font-medium tracking-tight text-white/90">{stat}</div>
					<div className="text-[11px] text-white/30 tracking-wide">{statLabel}</div>
				</div>
			</div>

			{/* Divider line */}
			<div className="my-5 h-px bg-white/[0.04]" />

			{/* Title and description */}
			<div className="space-y-2.5">
				<h3 className="text-[22px] font-medium tracking-tight text-white">{title}</h3>
				<p className="text-[15px] text-white/40 leading-[1.65]">{description}</p>
			</div>

			{/* Learn more link */}
			<a
				href={href}
				className="mt-5 inline-flex items-center gap-1.5 text-[15px] text-white/40 hover:text-gold hover:underline transition-colors duration-200"
			>
				Learn more
				<svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="transition-transform duration-200 group-hover:translate-x-0.5">
					<path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			</a>
		</motion.div>
	);
}

// Slot-machine style digit that animates when value changes
function SlotDigit({ digit, delay = 0 }: { digit: string; delay?: number }) {
	return (
		<span className="inline-block overflow-hidden h-[1.15em] relative" style={{ width: digit === "," ? "0.35em" : "0.62em" }}>
			<AnimatePresence mode="popLayout">
				<motion.span
					key={digit}
					initial={{ y: "100%", opacity: 0 }}
					animate={{ y: "0%", opacity: 1 }}
					exit={{ y: "-100%", opacity: 0 }}
					transition={{
						duration: 0.3,
						delay,
						ease: [0.25, 1, 0.5, 1],
					}}
					className="absolute inset-0 flex items-center justify-center"
				>
					{digit}
				</motion.span>
			</AnimatePresence>
		</span>
	);
}

// Stacked round avatars — unique seeds, tighter overlap
const CREATOR_AVATARS = [
	"https://i.pravatar.cc/80?u=ada-lovelace",
	"https://i.pravatar.cc/80?u=linus-torvalds",
	"https://i.pravatar.cc/80?u=grace-hopper",
	"https://i.pravatar.cc/80?u=djikstra",
	"https://i.pravatar.cc/80?u=margaret-hamilton",
];

const SUSTAINER_AVATARS = [
	"https://i.pravatar.cc/80?u=satoshi-nakamoto",
	"https://i.pravatar.cc/80?u=vitalik-buterin",
	"https://i.pravatar.cc/80?u=balaji-srinivasan",
	"https://i.pravatar.cc/80?u=juan-benet",
	"https://i.pravatar.cc/80?u=moxie-marlinspike",
];

function AvatarStack({ avatars }: { avatars: string[] }) {
	return (
		<div className="flex">
			{avatars.map((src, i) => (
				<img
					key={src}
					src={src}
					alt=""
					className="w-8 h-8 rounded-full border-2 border-[#0f0f0f] object-cover"
					style={{ zIndex: avatars.length - i, marginLeft: i === 0 ? 0 : -6 - i * 2 }}
					loading="lazy"
				/>
			))}
		</div>
	);
}

// Stacked rounded-square app thumbnails — tighter overlap
const APP_THUMBNAILS = [
	"https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1614851099511-773084f6911d?w=80&h=80&fit=crop",
];

function AppThumbnailStack() {
	return (
		<div className="flex">
			{APP_THUMBNAILS.map((src, i) => (
				<img
					key={src}
					src={src}
					alt=""
					className="w-8 h-8 rounded-[7px] border-2 border-[#0f0f0f] object-cover"
					style={{ zIndex: APP_THUMBNAILS.length - i, marginLeft: i === 0 ? 0 : -6 - i * 2 }}
					loading="lazy"
				/>
			))}
		</div>
	);
}

// Single stat column: stack + number + label
function StatColumn({
	value,
	label,
	children,
}: {
	value: number;
	label: string;
	children: React.ReactNode;
}) {
	const formatted = value.toLocaleString();
	const digits = formatted.split("");

	return (
		<div className="flex items-center gap-3">
			{children}
			<div>
				<div className="text-[17px] font-medium tracking-tight text-white tabular-nums font-mono flex leading-none">
					{digits.map((d, i) => (
						<SlotDigit key={`${i}-pos`} digit={d} delay={i * 0.03} />
					))}
				</div>
				<span className="text-[11px] text-white/35 block tracking-wide">{label}</span>
			</div>
		</div>
	);
}

// Live community counter — creators fastest, sustainers medium, apps slowest
function CommunityCounter() {
	const [creators, setCreators] = useState(2_847);
	const [sustainers, setSustainers] = useState(867);
	const [apps, setApps] = useState(419);

	// Creators: avg ~1.7s
	useEffect(() => {
		const tick = () => {
			const delay = 1000 + Math.random() * 1400;
			return setTimeout(() => {
				setCreators((c) => c + 1);
				timeoutRef = tick();
			}, delay);
		};
		let timeoutRef = tick();
		return () => clearTimeout(timeoutRef);
	}, []);

	// Sustainers: avg ~5s
	useEffect(() => {
		const tick = () => {
			const delay = 3000 + Math.random() * 4000;
			return setTimeout(() => {
				setSustainers((s) => s + 1);
				timeoutRef = tick();
			}, delay);
		};
		let timeoutRef = tick();
		return () => clearTimeout(timeoutRef);
	}, []);

	// Apps: avg ~14s
	useEffect(() => {
		const tick = () => {
			const delay = 9000 + Math.random() * 10000;
			return setTimeout(() => {
				setApps((a) => a + 1);
				timeoutRef = tick();
			}, delay);
		};
		let timeoutRef = tick();
		return () => clearTimeout(timeoutRef);
	}, []);

	return (
		<div className="inline-flex items-center gap-7 px-6 py-4">
			<StatColumn value={creators} label="Creators">
				<AvatarStack avatars={CREATOR_AVATARS} />
			</StatColumn>
			<div className="h-8 w-px bg-white/[0.06]" />
			<StatColumn value={sustainers} label="Sustainers">
				<AvatarStack avatars={SUSTAINER_AVATARS} />
			</StatColumn>
			<div className="h-8 w-px bg-white/[0.06]" />
			<StatColumn value={apps} label="Apps">
				<AppThumbnailStack />
			</StatColumn>
		</div>
	);
}

export default function LandingPageRoute() {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const headerHidden = useScrollDirection(scrollContainerRef);
	const globeContainerRef = useRef<HTMLDivElement>(null);
	const [isGlobeVisible, setIsGlobeVisible] = useState(true);

	// Pause globe animation when off-screen
	useEffect(() => {
		const container = globeContainerRef.current;
		if (!container) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsGlobeVisible(entry.isIntersecting);
			},
			{ threshold: 0 }
		);

		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	return (
		<div ref={scrollContainerRef} className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-[#0a0a0a] text-white pt-5">

			{/* ===== HEADER ===== */}
			<motion.header
				initial={{ y: 0 }}
				animate={{ y: headerHidden ? "-150%" : 0 }}
				transition={{
					duration: 0.4,
					ease: [0.7, 0, 0.2, 1], // aggressive ease-out with fast start
				}}
				className="sticky top-0 z-40 px-[15%] py-3"
			>
				<div className="relative bg-[#050505]/80 backdrop-blur-xl rounded-xl overflow-hidden">
					{/* Grain texture on header only */}
					<div
						className="pointer-events-none absolute inset-0 opacity-[0.08]"
						style={{
							backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
						}}
					/>
					<nav className="relative flex items-center justify-between px-5 py-3">
					<motion.a
						href="/"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5 }}
						className="flex items-center"
					>
						<img src={logoLockup} alt="Basilica" className="h-[22px]" />
					</motion.a>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.1 }}
						className="flex items-center gap-6"
					>
						<a href="#about" className="text-sm text-white/60 hover:text-white transition-colors">
							About
						</a>
						<a href="/blog" className="text-sm text-white/60 hover:text-white transition-colors">
							Blog
						</a>
						<motion.a
							href="#download"
							whileHover={{ backgroundColor: "rgba(255,255,255,0.2)" }}
							whileTap={{ backgroundColor: "rgba(255,255,255,0.25)", y: 1 }}
							transition={{ duration: 0.1 }}
							className="text-sm text-white font-normal px-4 py-2 rounded-lg bg-white/10 backdrop-blur-md"
						>
							Download
						</motion.a>
					</motion.div>
					</nav>
				</div>
			</motion.header>

			{/* ===== HERO ===== */}
			<section className="py-24 px-[15%]">
				<div className="flex items-center justify-center gap-[clamp(64px,8vw,200px)]">
					{/* Left - Text content */}
					<div className="shrink-0">
						<motion.h1
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="font-sans text-[32px] lg:text-[42px] font-normal tracking-tight text-white"
						>
							Hello again, world.
						</motion.h1>

						{/* Subtitle */}
						<motion.p
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.35 }}
							className="mt-6 max-w-lg font-sans text-xl leading-relaxed text-white/50"
						>
							Software that runs on networks, not<br />corporations. Self-sustaining infrastructure<br />for the next generation of applications.
						</motion.p>

						{/* Download Buttons */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.5 }}
							className="mt-8 flex gap-2"
						>
							<Button icon={AppleLogo}>macOS</Button>
							<Button icon={WindowsLogo}>Windows</Button>
							<Button icon={LinuxLogo}>Linux</Button>
						</motion.div>
					</div>

					{/* Right - 3D Earth Globe */}
					<motion.div
						ref={globeContainerRef}
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
						className="flex-shrink-0 h-[464px] w-[464px]"
					>
						<Suspense fallback={<GlobeSpinner />}>
							<Earth
								width={464}
								height={464}
								noBackground
								enablePointerInteraction={true}
								atmosphereAltitude={0.1}
								isPaused={!isGlobeVisible}
							/>
						</Suspense>
					</motion.div>
				</div>
			</section>

			{/* ===== PRESS LOGOS ===== */}
			<section className="py-24 px-[15%]">
				<motion.div
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6, delay: 0.1 }}
					className="flex items-center justify-center gap-20 flex-wrap"
				>
					<img
						src="https://cdn.brandfetch.io/idKIPMiOhQ/theme/dark/logo.svg"
						alt="The Block"
						className="h-5 brightness-0 invert opacity-40"
						loading="lazy"
					/>
					<img
						src="https://cdn.brandfetch.io/idqY_V9808/theme/dark/logo.svg"
						alt="CoinDesk"
						className="h-5 brightness-0 invert opacity-40"
						loading="lazy"
					/>
					<img
						src="https://cdn.brandfetch.io/ido44e0GAr/w/400/h/302/theme/light/logo.png"
						alt="Decrypt"
						className="h-5 brightness-0 invert opacity-40"
						loading="lazy"
					/>
					<img
						src="https://cdn.brandfetch.io/id4_c58dqo/theme/dark/logo.svg"
						alt="Messari"
						className="h-5 brightness-0 invert opacity-40"
						loading="lazy"
					/>
					<img
						src="https://cdn.brandfetch.io/idy-VbkbWC/theme/dark/logo.svg"
						alt="Bankless"
						className="h-5 brightness-0 invert opacity-40"
						loading="lazy"
					/>
					<img
						src="https://cdn.brandfetch.io/idHp5VdAks/theme/dark/logo.svg"
						alt="Defiant"
						className="h-5 brightness-0 invert opacity-40"
						loading="lazy"
					/>
				</motion.div>
			</section>

			{/* ===== THE NEXT FRONTIER ===== */}
			<section className="relative py-24 px-[15%]">
				<div className="mx-auto max-w-5xl">
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1 }}
						className="text-center text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white leading-[1.2]"
					>
						Self-sustaining software.
					</motion.h2>
					<motion.p
						initial={{ opacity: 0, y: 15 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="mt-6 text-center text-xl text-white/50 leading-relaxed max-w-3xl mx-auto"
					>
						Applications that don't depend on any single company to keep running. Built from the ground up with breakthrough cryptography, novel consensus mechanisms, and economic models that ensure the people who build things are the ones who&nbsp;benefit.
					</motion.p>

					{/* Showcase carousel */}
					<div className="mt-14 -mx-[15%] px-[15%] overflow-hidden">
						<motion.div
							initial={{ opacity: 0 }}
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6, delay: 0.3 }}
							className="flex gap-4 overflow-x-auto pb-4 scrollbar-none"
							style={{ scrollbarWidth: "none" }}
						>
							{[
								{
									name: "Persistent Game Worlds",
									category: "Gaming",
									description: "Multiplayer worlds that run across player devices. No central server to maintain or shut down.",
									image: "https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=600&h=340&fit=crop",
									creator: "maxis.dev",
									avatar: "https://i.pravatar.cc/150?u=maxis",
									likes: 847,
									remixes: 23,
								},
								{
									name: "Sample Libraries",
									category: "Audio",
									description: "Share sounds with automatic attribution. When someone uses your sample, you're credited.",
									image: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=600&h=340&fit=crop",
									creator: "loophaus",
									avatar: "https://i.pravatar.cc/150?u=loophaus",
									likes: 2341,
									remixes: 156,
								},
								{
									name: "Design Systems",
									category: "Components",
									description: "Component libraries that track usage. See where your work ends up.",
									image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&h=340&fit=crop",
									creator: "una.ui",
									avatar: "https://i.pravatar.cc/150?u=una",
									likes: 1129,
									remixes: 89,
								},
								{
									name: "Collaborative Wikis",
									category: "Knowledge",
									description: "Shared knowledge bases where every contribution is tracked and attributed.",
									image: "https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=600&h=340&fit=crop",
									creator: "archivists",
									avatar: "https://i.pravatar.cc/150?u=archivists",
									likes: 562,
									remixes: 34,
								},
								{
									name: "Mod Ecosystems",
									category: "Gaming",
									description: "Game modifications with clear provenance. Modders keep control of their work.",
									image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=600&h=340&fit=crop",
									creator: "nexus_",
									avatar: "https://i.pravatar.cc/150?u=nexus",
									likes: 3891,
									remixes: 412,
								},
								{
									name: "Research Datasets",
									category: "Science",
									description: "Open data with proper citation trails. Contributions to research properly tracked.",
									image: "https://images.unsplash.com/photo-1614851099511-773084f6911d?w=600&h=340&fit=crop",
									creator: "openscience",
									avatar: "https://i.pravatar.cc/150?u=openscience",
									likes: 724,
									remixes: 67,
								},
							].map((app, i) => (
								<motion.div
									key={app.name}
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
									className="group flex-shrink-0 w-[280px] rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-200"
								>
									{/* Thumbnail */}
									<div className="relative aspect-video overflow-hidden">
										<img
											src={app.image}
											alt={app.name}
											className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
											loading="lazy"
										/>
										<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
										<span className="absolute bottom-2 left-2 text-[11px] font-medium text-white/70 bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded-full">
											{app.category}
										</span>
									</div>
									{/* Content */}
									<div className="p-4">
										<h3 className="text-lg font-medium text-white">{app.name}</h3>
										<p className="mt-1.5 text-[14px] text-white/50 leading-relaxed line-clamp-2">
											{app.description}
										</p>
									</div>
									{/* Creator row */}
									<div className="px-4 pb-4 flex items-center justify-between">
										<div className="flex items-center gap-2">
											<img
												src={app.avatar}
												alt={app.creator}
												className="w-5 h-5 rounded-full"
											/>
											<span className="text-[13px] text-white/40">{app.creator}</span>
										</div>
										<div className="flex items-center gap-3 text-white/30">
											<span className="flex items-center gap-1 text-[12px]">
												<Heart size={12} weight="fill" />
												{app.likes.toLocaleString()}
											</span>
											<span className="flex items-center gap-1 text-[12px]">
												<ArrowsClockwise size={12} />
												{app.remixes}
											</span>
										</div>
									</div>
								</motion.div>
							))}
						</motion.div>
					</div>
				</div>
			</section>

			{/* ===== A SELF-SUSTAINING ALTERNATIVE ===== */}
			<section className="relative py-24 px-[15%]">
				<div className="mx-auto max-w-5xl">
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1 }}
						className="mt-4 text-center text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white"
					>
						Networks, not corporations.
					</motion.h2>
					<motion.p
						initial={{ opacity: 0, y: 15 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="mt-6 text-center text-xl text-white/50 max-w-3xl mx-auto leading-relaxed mb-14"
					>
						Bitcoin proved money can exist without banks. Prediction markets proved truth can emerge without institutions. Basilica extends this to software—applications that run on participant networks rather than corporate&nbsp;infrastructure.
					</motion.p>

					<ComparisonTable />
				</div>
			</section>

			{/* ===== FOUR PILLARS ===== */}
			<section className="relative py-24 px-[15%]">
				<div className="mx-auto max-w-5xl">
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1 }}
						className="mt-4 text-center text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white"
					>
						Built from the ground up.
					</motion.h2>
					<motion.p
						initial={{ opacity: 0, y: 15 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="mt-6 text-center text-xl text-white/50 max-w-3xl mx-auto leading-relaxed"
					>
						No forks, no legacy. Every layer—cryptographic primitives, consensus, economics—designed from first principles for a new class of&nbsp;software.
					</motion.p>

					{/* Pillar cards grid */}
					<div className="grid grid-cols-2 gap-5 mt-14 max-w-[680px] mx-auto">
						<PillarCard
							icon={Fingerprint}
							title="Self-defending"
							description="Signature aggregation and elastic validation with post-quantum cryptography. No central server, no single point of&nbsp;failure."
							stat="256-bit"
							statLabel="encryption"
							href="/docs/security"
							delay={0}
						/>
						<PillarCard
							icon={Boxes}
							title="Self-hosting"
							description="An integrated filesystem designed for exabytes of rich media content. Ten thousand times more efficient than current&nbsp;alternatives."
							stat="10,000×"
							statLabel="efficiency"
							href="/docs/storage"
							delay={0.1}
						/>
						<PillarCard
							icon={Network}
							title="Self-ordering"
							description="Novel proof of PageRank enables fully decentralized consensus. Quality surfaces through mathematics rather than human&nbsp;curation."
							stat="50k"
							statLabel="tps per node"
							href="/docs/consensus"
							delay={0.2}
						/>
						<PillarCard
							icon={Gem}
							title="Self-funding"
							description="Block rewards distributed to all network participants. Creators and contributors earn real revenue starting from day&nbsp;one."
							stat="100%"
							statLabel="to creators"
							href="/docs/economics"
							delay={0.3}
						/>
					</div>
				</div>
			</section>

			{/* ===== BUILD WITH US ===== */}
			<section className="relative py-24 px-[15%]">
				<div className="mx-auto max-w-3xl text-center">
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1 }}
						className="text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white"
					>
						Build what matters.
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 15 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="mt-6 text-xl text-white/45 leading-relaxed max-w-2xl mx-auto"
					>
						Open source. Peer-to-peer. Post-quantum. Build on a network where creators own what they ship and contributors earn from day&nbsp;one.
					</motion.p>

					{/* Live counters */}
					<motion.div
						initial={{ opacity: 0, y: 15 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="mt-12"
					>
						<CommunityCounter />
					</motion.div>

					{/* Email signup */}
					<motion.form
						initial={{ opacity: 0, y: 15 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.4 }}
						className="mt-10 flex items-center gap-3 max-w-md mx-auto"
						onSubmit={(e) => e.preventDefault()}
					>
						<input
							type="email"
							placeholder="enter your email"
							className="flex-1 h-[48px] px-5 rounded-xl bg-white/5 text-white placeholder:text-white/30 text-base focus:outline-none focus:bg-white/[0.07] transition-all duration-200"
						/>
						<Button type="submit" disabled>
							Subscribe
						</Button>
					</motion.form>

				</div>
			</section>

			{/* ===== FOOTER ===== */}
			<footer className="relative mt-24 pt-8 pb-20 px-[15%] border-t border-white/5">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<BasilicaLogo size={16} className="text-white/40" />
						<span className="text-sm text-white/40">© 2025 Basilica Digital Inc.</span>
					</div>

					<div className="flex items-center gap-5">
						<a href="/privacy" className="text-sm text-white/40 hover:text-white transition-colors duration-150">
							Privacy
						</a>
						<a href="/terms" className="text-sm text-white/40 hover:text-white transition-colors duration-150">
							Terms
						</a>
						<a href="https://github.com/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<GithubLogo size={20} weight="regular" className="group-hover:hidden" />
							<GithubLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
						<a href="https://discord.gg/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<DiscordLogo size={20} weight="regular" className="group-hover:hidden" />
							<DiscordLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
						<a href="https://x.com/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<XLogo size={20} weight="regular" className="group-hover:hidden" />
							<XLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
					</div>
				</div>
			</footer>
		</div>
	);
}
